import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const validationSchema = Yup.object({
  block_label: Yup.string().required("Section label is required"),
  description: Yup.string(),
});

const NewBlockDialog = ({
  open,
  handleClose,
  getBlocks,
  searchBlock,
  selectedBlock,
  setPage,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [createBlockMessage, setCreateBlockMessage] = useState({
    type: "",
    message: "",
  });

  const formik = useFormik({
    initialValues: {
      block_label: selectedBlock?.block_label ?? "",
      description: selectedBlock?.description ?? "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setCreateBlockMessage({ type: "", message: "" });
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-blocks/${selectedBlock ? "update" : "create"}`,
          {
            ...values,
            currentSession: currentSession,
            ...(selectedBlock ? { block_id: selectedBlock?.block_id } : {}),
          },
          {
            withCredentials: true,
          },
        );

        if (response?.data?.success) {
          setCreateBlockMessage({
            type: "success",
            message:
              response?.data?.message ||
              `Form section ${selectedBlock ? "updated" : "created"} successfully`,
          });
          resetForm();
          getBlocks(searchBlock, 1);
          setPage(1);
          handleClose();
        }
      } catch (error) {
        setCreateBlockMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            `An error occurred while ${selectedBlock ? "updating" : "creating"} section`,
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    resetForm,
  } = formik;

  const handleCloseModal = () => {
    resetForm();
    setCreateBlockMessage({ type: "", message: "" });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {selectedBlock ? "Update" : "Create"} Form Section
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCloseModal}
          disabled={isSubmitting}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <FormikProvider value={formik}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Section Label"
                  name="block_label"
                  value={values.block_label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.block_label && Boolean(errors.block_label)}
                  helperText={touched.block_label && errors.block_label}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Stack>

              {createBlockMessage?.message && (
                <Alert
                  severity={createBlockMessage?.type}
                  sx={{ borderRadius: 2 }}
                >
                  {createBlockMessage?.message}
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              variant="text"
              disabled={isSubmitting}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {isSubmitting ? "Saving…" : "Save Section"}
            </Button>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

export default NewBlockDialog;
